import { useEffect, useMemo, useState } from "react";
import { cancelJob, getJobSchedules, getJobs, runJobSchedule, updateJobSchedule } from "@/api/endpoints";
import { useApi } from "@/hooks/useApi";
import { StatusBadge } from "@/components/StatusBadge";

function formatTimestamp(value: string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function formatDuration(start: string, end?: string | null) {
  const startTime = Date.parse(start);
  const endTime = end ? Date.parse(end) : Date.now();
  if (Number.isNaN(startTime) || Number.isNaN(endTime) || endTime < startTime) return "-";

  const totalSeconds = Math.max(0, Math.round((endTime - startTime) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatJobProgress(jobType: string, progress: { current: number; total: number; percent: number } | null) {
  if (!progress) return "-";
  if (jobType === "startup_scan") {
    return `${progress.percent}% (startup progress)`;
  }
  return `${progress.percent}% (${progress.current}/${progress.total})`;
}

export function JobsPage() {
  const schedules = useApi(() => getJobSchedules(), []);
  const jobs = useApi(() => getJobs({ limit: 50 }), []);
  const refetchSchedules = schedules.refetch;
  const refetchJobs = jobs.refetch;
  const [intervalDrafts, setIntervalDrafts] = useState<Record<string, string>>({});
  const [pauseDrafts, setPauseDrafts] = useState<Record<string, string>>({});
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      refetchSchedules().catch(() => {});
      refetchJobs().catch(() => {});
    }, 3000);
    return () => window.clearInterval(timer);
  }, [refetchJobs, refetchSchedules]);

  useEffect(() => {
    const nextIntervals: Record<string, string> = {};
    const nextPauses: Record<string, string> = {};
    for (const schedule of schedules.data?.data ?? []) {
      nextIntervals[schedule.id] = schedule.attributes.interval;
      nextPauses[schedule.id] = "1h";
    }
    if (Object.keys(nextIntervals).length > 0) {
      setIntervalDrafts((current) => ({ ...nextIntervals, ...current }));
      setPauseDrafts((current) => ({ ...nextPauses, ...current }));
    }
  }, [schedules.data]);

  const scheduleRows = useMemo(() => schedules.data?.data ?? [], [schedules.data]);
  const jobRows = useMemo(() => jobs.data?.data ?? [], [jobs.data]);
  const loading = (schedules.loading && !schedules.data) || (jobs.loading && !jobs.data);

  const runningScheduleIds = useMemo(() => {
    return new Set(
      scheduleRows
        .filter((schedule) => schedule.attributes.state === "running")
        .map((schedule) => schedule.id),
    );
  }, [scheduleRows]);

  async function refresh() {
    await Promise.all([
      refetchSchedules().catch(() => null),
      refetchJobs().catch(() => null),
    ]);
  }

  async function handleSaveInterval(scheduleId: string) {
    setActiveAction(`save:${scheduleId}`);
    setActionError(null);
    try {
      await updateJobSchedule(scheduleId, { interval: intervalDrafts[scheduleId] });
      await refresh();
    } catch {
      setActionError("Failed to save schedule interval");
    } finally {
      setActiveAction(null);
    }
  }

  async function handleToggleEnabled(scheduleId: string, enabled: boolean) {
    setActiveAction(`toggle:${scheduleId}`);
    setActionError(null);
    try {
      await updateJobSchedule(scheduleId, { enabled: !enabled });
      await refresh();
    } catch {
      setActionError("Failed to update schedule state");
    } finally {
      setActiveAction(null);
    }
  }

  async function handlePause(scheduleId: string) {
    setActiveAction(`pause:${scheduleId}`);
    setActionError(null);
    try {
      await updateJobSchedule(scheduleId, { pause_for: pauseDrafts[scheduleId] || "1h" });
      await refresh();
    } catch {
      setActionError("Failed to pause schedule");
    } finally {
      setActiveAction(null);
    }
  }

  async function handleResume(scheduleId: string) {
    setActiveAction(`resume:${scheduleId}`);
    setActionError(null);
    try {
      await updateJobSchedule(scheduleId, { pause_for: null });
      await refresh();
    } catch {
      setActionError("Failed to resume schedule");
    } finally {
      setActiveAction(null);
    }
  }

  async function handleRunNow(scheduleId: string) {
    setActiveAction(`run:${scheduleId}`);
    setActionError(null);
    try {
      await runJobSchedule(scheduleId);
      await refresh();
    } catch {
      setActionError("Failed to start scheduled job");
    } finally {
      setActiveAction(null);
    }
  }

  async function handleCancel(jobId: string) {
    setActiveAction(`cancel:${jobId}`);
    setActionError(null);
    try {
      await cancelJob(jobId);
      await refresh();
    } catch {
      setActionError("Failed to cancel job");
    } finally {
      setActiveAction(null);
    }
  }

  if (loading) return <p>Loading...</p>;
  if (schedules.error || jobs.error) return <p className="error-message">Failed to load jobs</p>;

  return (
    <div className="admin-page">
      <h1>Jobs</h1>
      <p className="settings-copy admin-page-copy">
        Recurring jobs run from saved schedules. Edit the interval, disable a schedule, or pause it for a bounded window without losing the definition.
      </p>
      {actionError && <p className="error-message">{actionError}</p>}

      <section className="jobs-section">
        <div className="jobs-section-header">
          <div>
            <h2>Schedules</h2>
            <p className="settings-note">One scan schedule per library, persisted on the server.</p>
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Target</th>
              <th>State</th>
              <th>Every</th>
              <th>Next Run</th>
              <th>Last Run</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {scheduleRows.length === 0 && (
              <tr>
                <td colSpan={7}>No schedules yet.</td>
              </tr>
            )}
            {scheduleRows.map((schedule) => (
              <tr key={schedule.id}>
                <td>{schedule.attributes.job_type}</td>
                <td>{schedule.attributes.target_name}</td>
                <td>
                  <StatusBadge status={schedule.attributes.state} />
                </td>
                <td>
                  <div className="jobs-control-row">
                    <input
                      className="settings-input jobs-duration-input"
                      type="text"
                      value={intervalDrafts[schedule.id] ?? schedule.attributes.interval}
                      onChange={(event) =>
                        setIntervalDrafts((current) => ({ ...current, [schedule.id]: event.target.value }))
                      }
                    />
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => handleSaveInterval(schedule.id)}
                      disabled={activeAction === `save:${schedule.id}` || runningScheduleIds.has(schedule.id)}
                    >
                      {activeAction === `save:${schedule.id}` ? "Saving..." : "Save"}
                    </button>
                  </div>
                </td>
                <td>{formatTimestamp(schedule.attributes.next_run_at)}</td>
                <td>
                  {formatTimestamp(schedule.attributes.last_run_at)}
                  {schedule.attributes.last_run_state && (
                    <div className="jobs-subtext">{schedule.attributes.last_run_state}</div>
                  )}
                </td>
                <td>
                  <div className="jobs-actions">
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => handleRunNow(schedule.id)}
                      disabled={activeAction === `run:${schedule.id}` || runningScheduleIds.has(schedule.id)}
                    >
                      {activeAction === `run:${schedule.id}` ? "Starting..." : "Run Now"}
                    </button>
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => handleToggleEnabled(schedule.id, schedule.attributes.enabled)}
                      disabled={activeAction === `toggle:${schedule.id}`}
                    >
                      {activeAction === `toggle:${schedule.id}`
                        ? "Saving..."
                        : schedule.attributes.enabled
                          ? "Disable"
                          : "Enable"}
                    </button>
                    <div className="jobs-control-row">
                      <input
                        className="settings-input jobs-duration-input"
                        type="text"
                        value={pauseDrafts[schedule.id] ?? "1h"}
                        onChange={(event) =>
                          setPauseDrafts((current) => ({ ...current, [schedule.id]: event.target.value }))
                        }
                      />
                      <button
                        type="button"
                        className="action-btn"
                        onClick={() => handlePause(schedule.id)}
                        disabled={activeAction === `pause:${schedule.id}`}
                      >
                        {activeAction === `pause:${schedule.id}` ? "Pausing..." : "Pause"}
                      </button>
                    </div>
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => handleResume(schedule.id)}
                      disabled={activeAction === `resume:${schedule.id}` || !schedule.attributes.paused_until}
                    >
                      {activeAction === `resume:${schedule.id}` ? "Resuming..." : "Resume"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="jobs-section">
        <div className="jobs-section-header">
          <div>
            <h2>Recent Runs</h2>
            <p className="settings-note">Execution history for manual runs, startup scans, and scheduled scans.</p>
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>State</th>
              <th>Progress</th>
              <th>Created</th>
              <th>Elapsed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobRows.length === 0 && (
              <tr>
                <td colSpan={6}>No jobs yet.</td>
              </tr>
            )}
            {jobRows.map((job) => (
              <tr key={job.id}>
                <td>{job.attributes.job_type}</td>
                <td>
                  <StatusBadge status={job.attributes.state} />
                </td>
                <td>
                  {formatJobProgress(job.attributes.job_type, job.attributes.progress)}
                </td>
                <td>{formatTimestamp(job.attributes.created_at)}</td>
                <td>{formatDuration(job.attributes.created_at, job.attributes.completed_at)}</td>
                <td>
                  {job.attributes.state === "running" ? (
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => handleCancel(job.id)}
                      disabled={activeAction === `cancel:${job.id}`}
                    >
                      {activeAction === `cancel:${job.id}` ? "Cancelling..." : "Cancel"}
                    </button>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
